if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        if (!this.form) return;

        this.idInput = this.form.querySelector('[name="id"]');
        if (this.idInput) this.idInput.disabled = false;

        this.submitButton = this.querySelector('[type="submit"], button[name="add"]');
        this.ctaLabel = this.querySelector('[data-cta-label]');

        this.cart =
          document.querySelector('cart-drawer') ||
          document.querySelector('cart-notification');

        this.variantRadios = this.querySelector('variant-radios');
        this.variants = this.getVariants(); // [] if no JSON exists

        // Theme expectations (global.js calls productForm.handleErrorMessage())
        this.hideErrors = this.dataset.hideErrors === 'true';
        this.error = false;

        // Bind once
        this.onSubmitHandler = this.onSubmitHandler.bind(this);
        this.onVariantRadiosChangeCapture = this.onVariantRadiosChangeCapture.bind(this);
        this.onClickCapture = this.onClickCapture.bind(this);

        this.form.addEventListener('submit', this.onSubmitHandler);

        // CRITICAL: capture change events BEFORE VariantRadios in global.js
        // We will stop propagation until all fieldsets are selected.
        if (this.variantRadios) {
          this.variantRadios.addEventListener('change', this.onVariantRadiosChangeCapture, true);
        }

        // Capture click: enforce id + CTA before other handlers
        this.submitButton?.addEventListener('click', this.onClickCapture, { capture: true });

        // Initial sync (and a couple of delayed syncs to beat other scripts)
        this.syncAll();
        setTimeout(() => this.syncAll(), 0);
        setTimeout(() => this.syncAll(), 50);
        setTimeout(() => this.syncAll(), 250);
      }

      disconnectedCallback() {
        if (this.variantRadios) {
          this.variantRadios.removeEventListener('change', this.onVariantRadiosChangeCapture, true);
        }
        this.submitButton?.removeEventListener('click', this.onClickCapture, { capture: true });
      }

      /* --------------------------
         Error handling (required by global.js VariantRadios)
      -------------------------- */

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      /* --------------------------
         Variant data
      -------------------------- */

      getVariants() {
        const json = this.querySelector('variant-radios script[type="application/json"]');
        if (!json) return [];
        try {
          const parsed = JSON.parse(json.textContent);
          return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
          console.warn('Variant JSON parse failed');
          return [];
        }
      }

      getSingleVariantId() {
        // Primary: Liquid attribute on <product-form>
        const ds = this.dataset.singleVariantId;
        if (ds) return String(ds);

        // Secondary: if JSON exists and has exactly one variant
        if (this.variants.length === 1) return String(this.variants[0].id);

        return '';
      }

      enforceVariantIdIfSingle() {
        if (!this.idInput) return;

        const singleId = this.getSingleVariantId();
        if (!singleId) return;

        if (!this.idInput.value) {
          this.idInput.value = singleId;
        }

        if (this.submitButton) {
          this.submitButton.type = 'submit';
          this.submitButton.removeAttribute('disabled');
          this.submitButton.removeAttribute('aria-disabled');
        }
      }

      /* --------------------------
         Variant selection helpers
      -------------------------- */

      getSelectedValuesFromFieldsets() {
        const values = [];
        const fieldsets = this.variantRadios ? Array.from(this.variantRadios.querySelectorAll('fieldset')) : [];
        fieldsets.forEach((fieldset) => {
          const checked = fieldset.querySelector('input[type="radio"]:checked');
          values.push(checked ? checked.value : null);
        });
        return values;
      }

      allOptionsSelected() {
        if (!this.variantRadios) return true;
        const fieldsets = Array.from(this.variantRadios.querySelectorAll('fieldset'));
        if (!fieldsets.length) return true;
        return fieldsets.every((fs) => !!fs.querySelector('input[type="radio"]:checked'));
      }

      findVariant(selected) {
        if (!selected || !selected[0]) return null;

        return (
          this.variants.find((v) => {
            if (v.option1 !== selected[0]) return false;
            if (v.option2 && v.option2 !== selected[1]) return false;
            if (v.option3 && v.option3 !== selected[2]) return false;
            return true;
          }) || null
        );
      }

      /* --------------------------
         Prevent global.js crash on partial selection
      -------------------------- */

      onVariantRadiosChangeCapture(evt) {
        // Clear any old errors on option interaction
        this.handleErrorMessage();

        // Single-variant: don’t interfere
        if (this.getSingleVariantId()) {
          this.enforceVariantIdIfSingle();
          this.syncCTA();
          return;
        }

        // If not all options are selected yet, STOP the event reaching VariantRadios.onVariantChange()
        // This avoids global.js updateOptions() exploding on undefined.value
        if (!this.allOptionsSelected()) {
          evt.stopImmediatePropagation();
          evt.stopPropagation();

          if (this.idInput) this.idInput.value = '';
          this.syncCTA();
          return;
        }

        // All options chosen: set idInput now (so submit works even if other scripts lag)
        const selected = this.getSelectedValuesFromFieldsets();
        const match = this.findVariant(selected);
        if (this.idInput) this.idInput.value = match ? String(match.id) : '';

        this.syncCTA();
        // allow event to continue so global.js can do URL/media/price updates
      }

      /* --------------------------
         CTA handling
      -------------------------- */

      syncCTA() {
        if (!this.ctaLabel) return;

        // Single variant => always Add to cart
        if (this.getSingleVariantId()) {
          this.ctaLabel.textContent = 'Add to cart';
          return;
        }

        // Multi variant
        if (this.idInput && this.idInput.value) {
          this.ctaLabel.textContent = 'Add to cart';
        } else {
          this.ctaLabel.textContent = 'Choose a Size';
        }
      }

      syncAll() {
        if (this._syncing) return;
        this._syncing = true;

        requestAnimationFrame(() => {
          try {
            this.enforceVariantIdIfSingle();
            this.syncCTA();
          } finally {
            this._syncing = false;
          }
        });
      }

      onClickCapture() {
        this.enforceVariantIdIfSingle();
        this.syncCTA();
      }

      /* --------------------------
         Add to cart (AJAX)
      -------------------------- */

      async onSubmitHandler(evt) {
        evt.preventDefault();

        this.enforceVariantIdIfSingle();
        this.syncCTA();

        if (!this.idInput || !this.idInput.value) return;

        this.handleErrorMessage();

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.headers['Accept'] = 'application/json';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        if (this.cart && this.cart.getSectionsToRender) {
          const sections = this.cart
            .getSectionsToRender()
            .map((s) => s.section || s.id)
            .filter(Boolean);

          if (sections.length) {
            formData.append('sections', sections.join(','));
            formData.append('sections_url', window.location.pathname);
          }
        }

        config.body = formData;

        const addUrl =
          (typeof routes !== 'undefined' && routes.cart_add_url ? routes.cart_add_url : '/cart/add') +
          '.js';

        try {
          const response = await fetch(addUrl, config);
          const data = await response.json();

          if (data.status) {
            this.handleErrorMessage(data.description || data.message || 'Unable to add to cart.');
            this.error = true;
            return;
          }

          this.error = false;

          if (this.cart && this.cart.renderContents) {
            this.cart.renderContents(data);
            this.cart.classList.remove('is-empty');
            this.cart.querySelector?.('cart-drawer-items')?.classList.remove('is-empty');
          } else {
            window.location = window.routes?.cart_url || '/cart';
          }
        } catch (error) {
          console.error('Add to cart exception:', error);
          this.handleErrorMessage('Unable to add to cart.');
        }
      }
    }
  );
}
